import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { AdminComponent } from './admin.component';

import { FeatureGuard } from '@app/core/guards/feature.guard';
import { UserSyncAuthGuard } from '@app/core/guards/user-sync.guard';
import { UserImportV2CanDeactivateGuard } from '@app/core/guards/user-import-v2-can-deactivated.guard';
import { AdminSocialConnectFeatureResolver } from '@app/resolvers/admin-social-connect-feature.resolver';

import { FEATURES } from '@app/utility/app.util';

const routes: Routes = [
  {
    path: '',
    component: AdminComponent,
    children: [
      {
        path: 'dashboard',
        loadChildren: () => import('app/modules/admin/dashboard/dashboard.module').then((m) => m.DashboardModule),
        data: { isHubspotEnabled: true },
      },
      {
        path: 'content-hub',
        loadChildren: () => import('app/modules/admin/content-hub/content-hub.module').then((m) => m.ContentHubModule),
        data: { isHubspotEnabled: true },
      },
      {
        path: 'posts/create',
        loadChildren: () => import('app/modules/admin/create-post/create-post.module').then((m) => m.CreatePostModule),
        data: { isHubspotEnabled: true },
      },
      {
        path: 'posts/native',
        loadChildren: () => import('app/modules/admin/create-native-post/create-native-post.module').then((m) => m.CreateNativePostModule),
        resolve: { isSocialConnectFeatureEnabled: AdminSocialConnectFeatureResolver },
        data: { isHubspotEnabled: true },
      },
      {
        path: 'messages',
        loadChildren: () => import('app/modules/admin/message-center/message-center.module').then((m) => m.MessageCenterModule),
        resolve: { isSocialConnectFeatureEnabled: AdminSocialConnectFeatureResolver },
        data: { isHubspotEnabled: true },
      },
      {
        path: 'posts',
        children: [
          {
            path: '',
            loadChildren: () => import('app/modules/admin/posts/posts.module').then((m) => m.PostsModule),
            data: {
              isHubspotEnabled: true,
              isAdmin: true,
            },
          },
          {
            path: 'schedule',
            loadChildren: () =>
              import('app/modules/admin/scheduled-posts/scheduled-posts.module').then((m) => m.ScheduledPostsModule),
            data: {
              isHubspotEnabled: true,
              isAdmin: true,
            },
            pathMatch: 'full',
          },
          {
            path: 'my-drafts',
            loadChildren: () => import('app/modules/admin/draft-posts/draft-posts.module').then((m) => m.DraftPostsModule),
            data: {
              isHubspotEnabled: true,
              isAdmin: true,
            },
          },
          { path: '**', redirectTo: 'regular' },
        ],
      },
      {
        path: 'users',
        loadChildren: () => import('app/modules/admin/users/users.module').then((m) => m.UsersModule),
        data: { isHubspotEnabled: true },
        pathMatch: 'full',
      },
      {
        path: 'user-groups',
        loadChildren: () => import('app/modules/admin/user-groups/user-groups.module').then((m) => m.UserGroupsModule),
        data: { isHubspotEnabled: true },
      },
      {
        path: 'sync-users',
        loadChildren: () => import('app/modules/admin/sync-users/sync-users.module').then((m) => m.SyncUsersModule),
        canActivate: [UserSyncAuthGuard],
        data: { isHubspotEnabled: true },
      },
      {
        path: 'users/import',
        loadChildren: () => import('app/modules/admin/users-import-v2/users-import-v2.module').then((m) => m.UsersImportV2Module),
        data: { isHubspotEnabled: true },
        pathMatch: 'full',
        canDeactivate: [UserImportV2CanDeactivateGuard],
      },
      {
        path: 'users/delete',
        loadChildren: () =>
          import('app/modules/admin/user-bulk-delete/user-bulk-delete.module').then((m) => m.UserBulkDeleteModule),
        data: { isHubspotEnabled: true },
        pathMatch: 'full',
      },
      {
        path: 'tags',
        loadChildren: () => import('app/modules/admin/tags/tags.module').then((m) => m.TagsModule),
        data: { isHubspotEnabled: true },
      },
      {
        path: 'departments',
        loadChildren: () => import('app/modules/admin/department/department.module').then((m) => m.DepartmentModule),
        data: { isHubspotEnabled: true },
      },
      {
        path: 'scoreboard',
        loadChildren: () => import('app/modules/admin/scoreboard/scoreboard.module').then((m) => m.ScoreboardModule),
        data: { isHubspotEnabled: true },
      },
      {
        path: 'autoflow',
        loadChildren: () => import('app/modules/admin/auto-flow/auto-flow.module').then((m) => m.SmartFlowModule),
        canActivate: [FeatureGuard],
        data: { isHubspotEnabled: true, feature: FEATURES.AUTO_FLOW },
      },
      {
        path: 'insights',
        loadChildren: () => import('app/modules/admin/overview/overview.module').then((m) => m.OverviewModule),
        data: { isHubspotEnabled: true },
      },
      {
        path: 'insights/benchmark',
        loadChildren: () => import('app/modules/admin/benchmark-report/benchmark.module').then((m) => m.BenchmarkModule),
        data: { isHubspotEnabled: true },
      },
      {
        path: 'insights/engagement',
        loadChildren: () => import('app/modules/admin/engagement/engagement.module').then((m) => m.EngagementModule),
        data: { isHubspotEnabled: true },
      },
      {
        path: 'insights/post-effectiveness',
        loadChildren: () =>
          import('app/modules/admin/post-effectiveness/post-effectiveness.module').then((m) => m.PostEffectivenessModule),
        data: { isHubspotEnabled: true },
      },
      {
        path: 'insights/usergroup-stats',
        loadChildren: () =>
          import('app/modules/admin/usergroup-stats/usergroup-stats.module').then((m) => m.UsergroupStatsModule),
        data: { isHubspotEnabled: true },
      },
      // {
      //   path: 'security/roles',
      //   loadChildren: () =>
      //     import('app/modules/admin/roles-and-permissions/roles-and-permissions.module').then((m) => m.RolesAndPermissionsModule),
      //   data: { isHubspotEnabled: true },
      // },
      {
        path: 'security/roles',
        redirectTo: 'settings/security/roles'
      },
      {
        path: 'settings',
        loadChildren: () => import('app/modules/admin/settings/settings.module').then((m) => m.SettingsModule),
        data: { isHubspotEnabled: true },
      },
      {
        path: 'email',
        children: [
          {
            path: 'template',
            loadChildren: () =>
              import('app/modules/admin/email-template/email-template.module').then((m) => m.EmailTemplateModule),
          },
        ],
        data: { isHubspotEnabled: true },
      },
      {
        path: 'media-library',
        loadChildren: () => import('app/modules/admin/media-library/media-library.module').then((m) => m.MediaLibraryModule),
        data: { isHubspotEnabled: true },
      },
      {
        path: 'account/settings',
        loadChildren: () => import('app/modules/admin/user-settings/user-settings.module').then((m) => m.UserSettingsModule),
        data: { isHubspotEnabled: true },
      },
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class AdminRoutingModule {}
